import React from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import { useEditSegmentView } from "../EditSegment.context";
import { ConditionGroupProps } from "./ConditionGroup";
import { getDisabledAttributeTypes } from "./ConditionGroup.helpers";

export function useConditionGroup(props: ConditionGroupProps) {
  const {
    index,
    onEmpty,
    prefixName,
    hideDivider,
    attributesTypes,
    maxConditions,
    hideAddConditionsAtMax,
  } = props;

  const { segmentConditionsInfo, editDisabled, editDisabledTooltip } =
    useEditSegmentView();

  const { fields, append, remove } = useFieldArray({
    name: `${prefixName}.conditions`,
  });

  const { trigger, watch, getValues } = useFormContext();
  const groupConditions = watch(`${prefixName}.conditions`);

  const addCondition = React.useCallback(() => {
    append({
      type: "base",
      attributeType: "",
      attributeName: "",
      criteria: "",
      value: {},
    });
    trigger();
  }, [append, trigger]);

  const deleteCondition = React.useCallback(
    (i: number) => {
      remove(i);
    },
    [remove]
  );

  // Signature of what SENDER / LINK_URL rows depend on, so the derived values
  // below stay referentially stable while unrelated fields are edited.
  const dependencySignature = Array.isArray(groupConditions)
    ? groupConditions
        .map((cond: any) => `${cond?.attributeType}|${cond?.attributeName}`)
        .join(",")
    : "";

  const disabledAttributeTypesByRow = React.useMemo(
    () =>
      fields.map((_, i) =>
        getDisabledAttributeTypes(getValues(`${prefixName}.conditions`), i)
      ),
    [dependencySignature, fields.length, getValues, prefixName]
  );

  // A SENDER / LINK_URL row whose Activity condition was changed or removed
  // is kept and flagged invalid (see Condition's attributeType rule) instead
  // of being silently deleted; re-validate whenever that dependency changes.
  const isFirstRender = React.useRef(true);
  React.useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    trigger();
  }, [dependencySignature, trigger]);

  React.useEffect(() => {
    if (fields.length === 0) {
      onEmpty?.(index);
    }
  }, [fields.length, index, onEmpty]);

  return {
    addCondition,
    deleteCondition,
    index,
    segmentConditionsInfo,
    fields,
    prefixName,
    hideDivider,
    attributesTypes,
    editDisabled,
    editDisabledTooltip,
    maxConditions,
    hideAddConditionsAtMax,
    disabledAttributeTypesByRow,
  };
}
